import { Component, OnInit, AfterViewInit, inject, NgZone, DOCUMENT } from '@angular/core';
import { RouterStateService } from './shared/router-state.service';
import { Observable } from 'rxjs';
import { tap, delay, takeWhile } from 'rxjs/operators';
import { BaseComponent } from './core';

import { Router, NavigationEnd } from '@angular/router';
import { GaService, ProductService, UtmStorageService } from './services';
import { AuthService, InquiryReplySocketService, InquiryReplyCreatedEventPayload } from './shared';
import { UDirectService } from './services/udirect.service';
import { maintenanceConfig } from './core/config/maintenance.config';

declare global {
  interface Window {
    Calendly: any;
  }
}

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false,
})
export class AppComponent extends BaseComponent implements OnInit, AfterViewInit {
  loadedCount: Observable<string[]>;
  isInquiryReplyConnected$: Observable<boolean>;
  private isInquiryReplyDialogOpen = false;
  private pendingInquiryReply: InquiryReplyCreatedEventPayload | null = null;
  isLoading = false;
  readonly isMaintenanceMode = maintenanceConfig.enabled;
  /** Fab 활성화 여부 */
  isFab = true;
  /** 터치 시작 */
  start;
  /** 터치 종료 */
  end;
  /** 로그인 여부에 따른 이미지 url */
  csFabSrc = '';

  isRouterUrlSignupSimplify: boolean;

  private document = inject(DOCUMENT);
  private routingState = inject(RouterStateService);
  private router = inject(Router);
  private ngZone = inject(NgZone);
  public productService = inject(ProductService);
  private authService = inject(AuthService);
  private inquiryReplySocketService = inject(InquiryReplySocketService);
  private uDirectService = inject(UDirectService);
  private gaService = inject(GaService);
  private utmStorage = inject(UtmStorageService);

  constructor() {
    super();
    this.isInquiryReplyConnected$ = this.inquiryReplySocketService.isConnected$;
    // 상용시 console.log 무효화
    this.routingState.loadRouting();
    setTimeout(() => {
      if (this.authService.getUser()) this.uDirectService.checkUDirectAccess();
    }, 300);
  }

  ngOnInit() {
    this.userDeviceInfo = this.deviceDetector.isDesktop() ? 'pc' : 'mobile';
    this.document.documentElement.lang = this.i18n.language;

    // 콜드 스타트 시점의 utm_* 쿼리 캡처 (last-touch)
    this.utmStorage.captureFromSearchParams(new URLSearchParams(window.location.search));

    const currentUser = this.authService.getUser();
    if (currentUser) {
      this.gaService.setUserContext(currentUser);
      const buyerId = this.inquiryReplySocketService.resolveBuyerId(currentUser);
      this.inquiryReplySocketService.connect(buyerId);
    }

    this.router.events.forEach((item) => {
      if (item instanceof NavigationEnd) {
        // 인앱 라우팅으로 utm_* 가 들어와도 last-touch 갱신
        const queryStart = item.urlAfterRedirects.indexOf('?');
        if (queryStart >= 0) {
          this.utmStorage.captureFromSearchParams(new URLSearchParams(item.urlAfterRedirects.slice(queryStart + 1)));
        }
        this.gaService.logPage(item.url);
      }
    });

    this.isRouterUrlSignupSimplify = this.router.url.includes('simplify');
    if (!this.isRouterUrlSignupSimplify) {
      // window.Calendly.initBadgeWidget({
      //   url: 'https://calendly.com/umma_craver',
      //   text: 'schedule a meeting',
      //   color: '#f23da6',
      //   textColor: '#ffffff',
      //   branding: false,
      // });
      // let t: any = document.querySelectorAll('.calendly-badge-widget')[0];
      // if (this.userDeviceInfo === 'pc') {
      //   t.classList.add('pc');
      // } else {
      //   t.classList.remove('pc');
      // }
    }

    this.csFabSrc = this.authService.getUser() ? 'chat-line' : 'mail-line';
    this.authService.onLoginSubject$.subscribe(() => {
      this.csFabSrc = this.authService.getUser() ? 'chat-line' : 'mail-line';
    });

    this.inquiryReplySocketService.inquiryReplyCreated$.pipe(takeWhile(() => this.isAlive)).subscribe((payload) => {
      if (this.isInquiryReplyDialogOpen) {
        return;
      }

      this.pendingInquiryReply = payload;
      this.isInquiryReplyDialogOpen = true;
      const dialog = this._openDialog({
        title: 'New response to your inquiry',
        message: 'You’ve received a reply<br/>to your inquiry.',

        close: () => {
          dialog.close();
          this.isInquiryReplyDialogOpen = false;
          const inquiryId = this.pendingInquiryReply?.inquiryId;
          this.pendingInquiryReply = null;
          if (inquiryId !== null && inquiryId !== undefined && inquiryId !== '') {
            this.router.navigate(['/my/inquiry', inquiryId]);
            return;
          }
          this.onClickCS();
        },
        cancel: () => {
          dialog.close();
          this.isInquiryReplyDialogOpen = false;
          this.pendingInquiryReply = null;
        },
      });
    });
  }

  ngAfterViewInit() {
    this.store
      .select('indicator')
      .pipe(
        delay(0),
        tap((apis) => {
          this.isLoading = apis && apis.length > 0;
        })
      )
      .subscribe();
  }

  /**
   * 플로팅 일괄 담기 활성화
   */
  onClickBulkProducts() {
    if (
      this.productService.selectProducts.length !== 0 &&
      !['/cart', '/order', '/order/select-gift', '/order/payment', '/order/complete'].includes(this.location.path())
    )
      this.productService.emitCloseModal();
  }

  /**
   * 플로팅 CS 아이콘 활성화
   */
  onClickCS() {
    if (this.authService.getUser()) {
      this.router.navigate(['/my/inquiry']);
    } else {
      window.location.href = 'mailto:cs@umma.io';
    }
  }

  /**
   * 플로팅 팝업 on/off
   */
  onFabPopup() {
    this.isFab = !this.isFab;
  }

  /**
   * fab 버튼 swiper 이벤트 제어
   * @param event touchstart | touchend
   * @returns
   */
  onFabSwiper(event) {
    console.log(event);
    if (event.type === 'touchstart') {
      this.start = event.changedTouches[0].clientX;
      return;
    } else if (event.type === 'touchend') this.end = event.changedTouches[0].clientX;

    // start < end 왼쪽 스와이프
    if (this.start + 20 < this.end) {
      // 오른쪽 스와이프
      this.isFab = false;
      this.start = undefined;
      this.end = undefined;
    } else if (this.start > this.end + 20) {
      // 왼쪽 스와이프
      this.isFab = true;
      this.start = undefined;
      this.end = undefined;
    }
  }
}
